import { computed, ref, Ref } from 'vue';
import i18n from '@/i18n';
import {
    getFieldValueByPID,
    getNoChoiceGuideValue,
    isHeroProductName,
} from '@/modules/ConfiguratorCombinations/helpers/CombinationsHelper';
import { CombinationsOptions, GlobalOptions } from '@/enums/global/GlobalOptions';
import {
    combinationsConfiguration,
    CombinationsConfigurationItem,
    combinationsConfiguratorSteps,
    shuttersFields,
    standardCanvasTemplate,
} from '@/modules/ConfiguratorCombinations/helpers/CombinationsConfiguration';
import { CombinationsConfiguratorFormData } from '@/modules/ConfiguratorCombinations/interfaces/CombinationsInterfaces';

const FLOOR_MOUNTING_BREZ_VALUE = 'Brez';
const HERO_GUIDE_CARRIER_50_70_VALUE = '50-70 mm';
const localeAwareI18n = i18n as typeof i18n & { locale: string };

interface UseCombinationsConfigOptions {
    /** All product rows in the configurator form. */
    productRowsFormData?: Ref<CombinationsConfiguratorFormData[]>;
    /** IDs (`activeProductFormId`) of the currently selected rows. Predicates only inspect these. */
    selectedProductFormIds?: Ref<number[]>;
}

/**
 * Owns product-driven configuration for the combinations configurator.
 *
 * Pure derivations of `productName`:
 *   - which configurator variant (`simple` / `standard`) drives the wizard
 *   - which steps are active
 *   - which checklist fields populate the table & dropdowns
 *   - product-specific canvas sizes, hero flags etc.
 *
 * Reactive state owned here:
 *   - `tableHeaderFields`    — header definitions used by the product table
 *   - `FilteredCombinationsOptions` — PID map seeded from the checklist config
 *
 * Helper functions intentionally stay in `CombinationsHelper.ts` (pure, reusable,
 * easy to unit-test). This composable only adds the reactive layer on top.
 */
export function useCombinationsConfig(productName: Ref<string>, options: UseCombinationsConfigOptions = {}) {
    const { productRowsFormData, selectedProductFormIds } = options;
    // ------------------------------------------------------------------------
    // Reactive state
    // ------------------------------------------------------------------------
    const tableHeaderFields = ref<any[]>([]);
    const FilteredCombinationsOptions = ref<any>({ ...CombinationsOptions });

    // ------------------------------------------------------------------------
    // Derived configuration
    // ------------------------------------------------------------------------
    const currentConfig = computed<CombinationsConfigurationItem | undefined>(() =>
        combinationsConfiguration.find((item) => item.products.includes(productName.value)),
    );

    const configuratorType = computed(() => {
        if (!currentConfig.value) {
            return 'standard';
        }

        return currentConfig.value?.configuratorType || 'simple';
    });

    const checklistFieldConfig = computed(() => currentConfig.value?.checklistFields ?? shuttersFields);

    const activeSteps = computed(() => {
        const totalSteps = currentConfig.value?.configuratorSteps || combinationsConfiguratorSteps.length;

        return combinationsConfiguratorSteps.slice(0, totalSteps).map((step) => ({
            ...step,
            description: localeAwareI18n.t(step.description, localeAwareI18n.locale),
        }));
    });

    const shouldAddCombinationInMultiposition = computed(() =>
        currentConfig.value ? currentConfig.value.shouldAddCombinationInMultiposition : true,
    );

    const shouldShowAssemblyCode = computed(() =>
        currentConfig.value ? currentConfig.value.showCombinationType : false,
    );

    const showCombinationType = computed(() => {
        if (!currentConfig.value) {
            return true;
        }

        return currentConfig.value.showCombinationType || productName.value === 'KARO';
    });

    const isHeroProduct = computed(() => isHeroProductName(productName.value));

    const noChoiceGuideValue = computed(() => getNoChoiceGuideValue(checklistFieldConfig.value));

    // ------------------------------------------------------------------------
    // Hero alternative-center-guide flags
    // ------------------------------------------------------------------------
    /**
     * Returns `true` when every selected product row has the given PID set to
     * `expectedValue`. Returns `false` when there are no selected rows or no
     * row form data / selection refs were supplied.
     */
    function allSelectedRowsHaveFieldValue(pid: string, expectedValue: string): boolean {
        const rows = productRowsFormData?.value;
        const ids = selectedProductFormIds?.value;
        if (!rows || !ids || ids.length === 0) {
            return false;
        }

        return ids.every((id) => getFieldValueByPID(rows, id, pid) === expectedValue);
    }

    /**
     * `true` when every selected row's FLOOR_MOUNTING_HERO_PID equals `value`.
     */
    function allFloorMountingHeroAre(value: string): boolean {
        return allSelectedRowsHaveFieldValue(GlobalOptions.FLOOR_MOUNTING_HERO_PID.toString(), value);
    }

    /**
     * `true` when every selected row's HERO_GUIDE_CARRIER_PID equals `value`.
     */
    // function allHeroGuideCarrierAre(value: string): boolean {
    //     return allSelectedRowsHaveFieldValue(GlobalOptions.HERO_GUIDE_CARRIER_PID.toString(), value);
    // }

    /**
     * Whether the Hero canvas should render the alternative-center guide image
     * (replacing the standard center guide). Rules:
     *     FLOOR_MOUNTING_HERO is 'Brez'
     *   - hero-2: every selected row's HERO_GUIDE_CARRIER is '50-70 mm'
     *   - other configurator types: never
     */
    const shouldUseAlternativeCenterGuide = computed<boolean>(() => {
        const type = configuratorType.value;

        if (type === 'hero-1') {
            return !allFloorMountingHeroAre(FLOOR_MOUNTING_BREZ_VALUE);
        }

        if (type === 'hero-2') {
            // return allHeroGuideCarrierAre(HERO_GUIDE_CARRIER_50_70_VALUE);
            return true; // Temporary: all guide carrier options currently use the alternative center guide, per new Hero rules
        }

        return false;
    });

    /**
     * Effective canvas template for the current product. When the Hero rules
     * above call for it, the standard `guides.center` image is swapped with
     * `guides.alternativeCenter` so downstream guide rendering is unchanged.
     */
    const canvasTemplateConfiguration = computed(() => {
        const base = currentConfig.value?.canvasTemplateConfiguration ?? standardCanvasTemplate;

        if (!shouldUseAlternativeCenterGuide.value || !base.guides.alternativeCenter) {
            return base;
        }

        const type = configuratorType.value;

        if (type === 'hero-1') {
            return {
                ...base,
                middleGuideOffsetX: base.alternativeMiddleGuideOffsetX,
                guides: {
                    ...base.guides,
                    center: base.guides.alternativeCenter,
                },
            };
        }

        return {
            ...base,
            guides: {
                ...base.guides,
                center: base.guides.alternativeCenter,
            },
        };
    });

    // ------------------------------------------------------------------------
    // Initialization
    // ------------------------------------------------------------------------
    /**
     * Seed `FilteredCombinationsOptions` PIDs and `tableHeaderFields` from the
     * checklist config for the current product. Call this once after the popup
     * opens, before rendering form data.
     */
    function setCorrectPIdsForFormData(): void {
        const updatedOptions: any = {};
        Object.keys(checklistFieldConfig.value).forEach((key) => {
            const fieldConfig = (checklistFieldConfig.value as any)[key];
            updatedOptions[key] = fieldConfig.pid;
        });

        FilteredCombinationsOptions.value = { ...FilteredCombinationsOptions.value, ...updatedOptions };
        tableHeaderFields.value = Object.values(checklistFieldConfig.value);
    }

    /**
     * Reset state owned by this composable. Call from the popup's `resetForm`.
     */
    function resetConfig(): void {
        tableHeaderFields.value = [];
        FilteredCombinationsOptions.value = { ...CombinationsOptions };
    }

    return {
        // state
        tableHeaderFields,
        FilteredCombinationsOptions,
        // derived
        currentConfig,
        configuratorType,
        checklistFieldConfig,
        activeSteps,
        shouldAddCombinationInMultiposition,
        shouldShowAssemblyCode,
        showCombinationType,
        isHeroProduct,
        canvasTemplateConfiguration,
        noChoiceGuideValue,
        // actions
        setCorrectPIdsForFormData,
        resetConfig,
    };
}
